import {
  isNotNil,
  isNumber,
  isPlainObject,
  isString,
  isFunction,
  isEmpty,
  isNil,
} from '@flowio/is';
import { PaymentMethodParameter, RequestPaymentParameters } from '../types';

function validateNumber(num?: unknown): num is number {
  return isNotNil(num) && isNumber(num) && num > 0;
}

function validateString(str?: unknown): str is string {
  return isNotNil(str) && isString(str) && !isEmpty(str);
}

function validateURL(url: string): boolean {
  return /^https?:\/\/(?:[\w.-]+\/?)+/.test(
    url,
  );
}

function getInvalidErrorText(paramName: string): string {
  return `Invalid \`${paramName}\` specified on payment request data`;
}

/**
 * @overview Returns the list of supported actions that are needed to support the PM actions in the
 * current environment.
 */
function getActionSupportForPaymentMethod(
  pm: PaymentMethodParameter,
): io.flow.v0.enums.PaymentActionType[] {
  switch (pm.id) {
    case 'ideal': return ['select_payment_option'];
    case 'klarna': return ['use_sdk_klarna_v1'];
    case 'paypal': return ['use_sdk_paypal'];
    default: return [];
  }
}

function validateRenderParams(rpp: RequestPaymentParameters): boolean {
  if (isNotNil(rpp.data.paymentMethod) && isNotNil(rpp.config.supportedActions)) {
    const requiredActions = getActionSupportForPaymentMethod(rpp.data.paymentMethod);
    if (
      requiredActions.length > 0
      && requiredActions.every((ra) => rpp.config.supportedActions!.includes(ra))
      && isNil(rpp.data.paymentMethod.render)
    ) {
      throw new Error('[data.paymentMethod.render] should be specified to allow payment method action handling.');
    }
  }
  return true;
}

function isRequestPaymentPaymentMethod(
  rppm: unknown,
): rppm is PaymentMethodParameter {
  const castPm = rppm as PaymentMethodParameter;
  if (!isPlainObject(castPm)) throw new Error(getInvalidErrorText('data.paymentMethod'));
  if (!validateString(castPm.id)) throw new Error(getInvalidErrorText('data.paymentMethod.id'));
  // TODO: validate individual Payment Method inputs

  return true;
}

function isRequestPaymentDataParameter(
  rpd: unknown,
): rpd is RequestPaymentParameters['data'] {
  const castData = (rpd as RequestPaymentParameters['data']);
  if (!validateNumber(castData.amount)) throw new Error(getInvalidErrorText('data.amount'));
  if (!validateString(castData.currency)) throw new Error(getInvalidErrorText('data.currency'));
  if (!validateString(castData.returnUrl) || !validateURL(castData.returnUrl)) throw new Error(getInvalidErrorText('data.returnUrl'));
  if (!isPlainObject(castData.orderInformation) || isEmpty(castData.orderInformation)) throw new Error(getInvalidErrorText('data.orderDetails'));

  if (isNotNil(castData.paymentMethod)) {
    isRequestPaymentPaymentMethod(castData.paymentMethod);
  }

  return true;
}

function isRequestPaymentConfigParameter(
  rpc: unknown,
): rpc is RequestPaymentParameters['config'] {
  const castConfig = rpc as RequestPaymentParameters['config'];
  if (!validateString(castConfig.organization)) throw new Error(getInvalidErrorText('config.organization'));
  if (!validateString(castConfig.locale)) throw new Error(getInvalidErrorText('config.locale'));
  if (!validateString(castConfig.sessionId)) throw new Error(getInvalidErrorText('config.sessionId'));

  return true;
}

/**
 * @description User type-guard that also acts as a runtime validator for the
 * RequestPaymentParameters type.
 */
export default function validateRequestPaymentParameters(
  rpp: unknown,
): rpp is RequestPaymentParameters {
  const castRpp = rpp as RequestPaymentParameters;
  const onPaymentMethodCompleteValid = isNotNil(
    castRpp.onPaymentMethodCompleted,
  )
    ? isFunction(castRpp.onPaymentMethodCompleted)
    : true;

  if (!(isPlainObject(castRpp.data) && isRequestPaymentDataParameter(castRpp.data))) {
    throw new Error(getInvalidErrorText('data'));
  }
  if (!(isPlainObject(castRpp.config) && isRequestPaymentConfigParameter(castRpp.config))) {
    throw new Error(getInvalidErrorText('config'));
  }
  if (!onPaymentMethodCompleteValid) {
    throw new Error(getInvalidErrorText('onPaymentMethodCompleted'));
  }

  validateRenderParams(castRpp);

  return true;
}
